import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import useGoogleLocationApi from "../Services/useGoogleLocationApi";
import MapLocationPicker from "./MapLocationPicker";
import { useGlobalContext } from "../Context/GlobalContext";
import {
  BriefcaseBusiness,
  Check,
  ChevronDown,
  Home,
  Loader2,
  LocateFixed,
  MapPin,
  RefreshCcw,
  Search,
  Plus,
  X,
} from "lucide-react";

import useAddress from "../Services/useAddress";
import { useProfile } from "../Services/Profile/useProfile";

const normalizeString = (value) => String(value ?? "").trim();

const normalizeMobileNumber = (value) => {
  let digits = String(value ?? "").replace(/\D/g, "");

  // +91 / 91XXXXXXXXXX
  if (digits.length === 12 && digits.startsWith("91")) {
    digits = digits.slice(2);
  }

  // 0XXXXXXXXXX
  if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
};

const isValidIndianMobile = (value) => {
  return /^[6-9]\d{9}$/.test(normalizeMobileNumber(value));
};

const extractPlaceSuggestions = (response) => {
  const source =
    response?.response ??
    response?.data ??
    response?.predictions ??
    response?.suggestions ??
    [];

  if (!Array.isArray(source)) {
    return [];
  }

  return source
    .map((item) => {
      const placeId = item?.placeId ?? item?.place_id ?? item?.id ?? "";

      const description =
        item?.description ??
        item?.address ??
        item?.formatted_address ??
        item?.text ??
        item?.name ??
        "";

      return {
        placeId: normalizeString(placeId),

        description: normalizeString(description),

        raw: item,
      };
    })
    .filter((item) => item.placeId && item.description);
};

const safelyParseJson = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getAddressText = (value) => {
  if (!value) {
    return "";
  }

  // ---------------------------------------------
  // STRING ADDRESS
  // ---------------------------------------------

  if (typeof value === "string") {
    const parsed = safelyParseJson(value);

    if (parsed && typeof parsed === "object") {
      return getAddressText(parsed);
    }

    return normalizeString(value);
  }

  // ---------------------------------------------
  // ADDRESS OBJECT
  // ---------------------------------------------

  if (typeof value !== "object") {
    return "";
  }

  const addressObject = value?.values || value;

  // Direct address string
  if (typeof addressObject?.address === "string") {
    return normalizeString(addressObject.address);
  }

  // Sometimes address itself is another object
  const nestedAddress =
    addressObject?.address && typeof addressObject.address === "object"
      ? addressObject.address?.values || addressObject.address
      : {};

  const parts = [
    addressObject?.full_address,
    addressObject?.fullAddress,
    addressObject?.address_line,
    addressObject?.addressLine,

    nestedAddress?.house_number,
    nestedAddress?.houseNumber,
    nestedAddress?.flat_number,
    nestedAddress?.flatNumber,
    nestedAddress?.address,
    nestedAddress?.address_line,
    nestedAddress?.addressLine,
    nestedAddress?.landmark_area,
    nestedAddress?.landmark,
    nestedAddress?.area,
    nestedAddress?.city,
    nestedAddress?.state,
    nestedAddress?.in_pin_code,
    nestedAddress?.pinCode,
    nestedAddress?.pincode,
  ]
    .map(normalizeString)
    .filter(Boolean);

  return parts.join(", ");
};

const removeDuplicateAddresses = (addresses = []) => {
  const addressMap = new Map();

  addresses.forEach((item) => {
    const address = normalizeString(item?.address);

    if (!address) {
      return;
    }

    const key = address.toLowerCase();

    const existingItem = addressMap.get(key);

    const currentLatitude =
      item?.latitude ??
      item?.lat ??
      item?.raw?.latitude ??
      item?.raw?.lat ??
      "";

    const currentLongitude =
      item?.longitude ??
      item?.lontitude ??
      item?.lng ??
      item?.raw?.longitude ??
      item?.raw?.lontitude ??
      item?.raw?.lng ??
      "";

    if (!existingItem) {
      addressMap.set(key, {
        ...item,
        address,
        latitude: currentLatitude,
        longitude: currentLongitude,
      });

      return;
    }

    const existingLatitude = existingItem?.latitude ?? "";

    const existingLongitude = existingItem?.longitude ?? "";

    // Duplicate address me jis record ke paas
    // coordinates hain, unko preserve karo.
    addressMap.set(key, {
      ...existingItem,
      ...item,

      address,

      isDefault: existingItem?.isDefault === true || item?.isDefault === true,

      latitude: currentLatitude || existingLatitude,

      longitude: currentLongitude || existingLongitude,
    });
  });

  return Array.from(addressMap.values());
};

const getLocationIcon = (label) => {
  const normalizedLabel = normalizeString(label).toLowerCase();

  if (normalizedLabel.includes("home")) return Home;
  if (normalizedLabel.includes("office") || normalizedLabel.includes("work")) {
    return BriefcaseBusiness;
  }

  return MapPin;
};

const getCurrentCoordinates = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Location service is not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          reject(new Error("Please allow location permission"));
          return;
        }

        reject(new Error("Unable to detect your current location"));
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,

        // Har click par fresh location maango
        maximumAge: 0,
      },
    );
  });
};

const getAddressFromCoordinates = async (latitude, longitude) => {
  const params = new URLSearchParams({
    lat: String(latitude),
    lon: String(longitude),
    format: "jsonv2",
    addressdetails: "1",
    zoom: "18",
  });

  const response = await fetch(
    `https://nominatim.openstreetmap.org/reverse?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Accept-Language": "en",
      },
    },
  );

  if (!response.ok) {
    throw new Error("Unable to fetch address for current location");
  }

  const data = await response.json();
  const addressData = data?.address || {};

  const displayAddress = normalizeString(data?.display_name);
  const city = normalizeString(
    addressData?.city ||
      addressData?.town ||
      addressData?.village ||
      addressData?.municipality ||
      addressData?.county,
  );
  const state = normalizeString(addressData?.state);
  const pinCode = normalizeString(addressData?.postcode);
  const landmark = normalizeString(
    addressData?.neighbourhood ||
      addressData?.suburb ||
      addressData?.road ||
      addressData?.quarter,
  );

  if (!displayAddress) {
    throw new Error("Address not found for current location");
  }

  return {
    addressLine: displayAddress,
    city,
    state,
    pinCode,
    landmark,
  };
};

const normalizeAddressItem = (item, customer, index) => {
  if (!item) return null;

  if (typeof item === "string") {
    const parsed = safelyParseJson(item);

    if (parsed && typeof parsed === "object") {
      return normalizeAddressItem(parsed?.values || parsed, customer, index);
    }

    const address = normalizeString(item);
    if (!address) return null;

    return {
      id: `customer-${customer?.id || "address"}-${index}`,
      address,
      favourite: normalizeString(customer?.tag) || "Delivery Address",
      isDefault: false,
      source: "customer",
    };
  }

const addressObject = item?.values || item;

const address = getAddressText(addressObject);
const coordinates = getAddressCoordinates(addressObject);

  if (!address) return null;

  return {
    id:
      addressObject?.id ||
      addressObject?.address_id ||
      `customer-${customer?.id || "address"}-${index}`,
    address,
    favourite:
      normalizeString(addressObject?.tag) ||
      normalizeString(addressObject?.address_tag) ||
      normalizeString(customer?.tag) ||
      "Delivery Address",
    isDefault: Boolean(
      addressObject?.isDefault ||
      addressObject?.is_default ||
      addressObject?.default_address,
    ),
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    source: "customer",
    raw: addressObject,
  };
};

const getAddressCoordinates = (value) => {
  if (!value) {
    return {
      latitude: "",
      longitude: "",
    };
  }

  let source = value;

  if (typeof source === "string") {
    const parsed = safelyParseJson(source);

    if (!parsed || typeof parsed !== "object") {
      return {
        latitude: "",
        longitude: "",
      };
    }

    source = parsed;
  }

  if (typeof source !== "object") {
    return {
      latitude: "",
      longitude: "",
    };
  }

  const addressObject = source?.values || source;

  const nestedAddress =
    addressObject?.address && typeof addressObject.address === "object"
      ? addressObject.address?.values || addressObject.address
      : {};

  return {
    latitude:
      addressObject?.latitude ??
      addressObject?.lat ??
      nestedAddress?.latitude ??
      nestedAddress?.lat ??
      "",

    longitude:
      addressObject?.longitude ??
      addressObject?.lontitude ??
      addressObject?.lng ??
      nestedAddress?.longitude ??
      nestedAddress?.lontitude ??
      nestedAddress?.lng ??
      "",
  };
};

const extractCustomerAddresses = (customers = []) => {
  const addresses = [];

  customers.forEach((customer, customerIndex) => {
  const defaultAddressValue =
    customer?.defaultAddress ?? customer?.default_address;

  const defaultAddress = getAddressText(defaultAddressValue);

 if (defaultAddress) {
   const defaultAddressObject =
     typeof defaultAddressValue === "string"
       ? safelyParseJson(defaultAddressValue) || {}
       : defaultAddressValue?.values || defaultAddressValue || {};

   const defaultCoordinates = getAddressCoordinates(defaultAddressValue);

   addresses.push({
     id: `default-${customer?.id || customerIndex}`,

     address: defaultAddress,

     favourite:
       normalizeString(defaultAddressObject?.tag) ||
       normalizeString(customer?.tag) ||
       "Delivery Address",

     isDefault: true,

     latitude:
       defaultCoordinates.latitude || customer?.latitude || customer?.lat || "",

     longitude:
       defaultCoordinates.longitude ||
       customer?.longitude ||
       customer?.lontitude ||
       customer?.lng ||
       "",

     source: "customer",

     raw: defaultAddressObject,
   });
 }

    const sourceAddresses = [
      ...(Array.isArray(customer?.address)
        ? customer.address
        : customer?.address
          ? [customer.address]
          : []),
      ...(Array.isArray(customer?.addresses) ? customer.addresses : []),
      ...(Array.isArray(customer?.deliveryAddresses)
        ? customer.deliveryAddresses
        : []),
      ...(Array.isArray(customer?.delivery_addresses)
        ? customer.delivery_addresses
        : []),
    ];

    sourceAddresses.forEach((item, addressIndex) => {
      const normalizedAddress = normalizeAddressItem(
        item,
        customer,
        `${customerIndex}-${addressIndex}`,
      );

      if (normalizedAddress) addresses.push(normalizedAddress);
    });
  });

  return removeDuplicateAddresses(addresses);
};

const normalizeMasterTags = (result) => {
  console.log("normalizeMasterTags Input:", result);

  const source = Array.isArray(result?.deliveryAddresses)
    ? result.deliveryAddresses
    : Array.isArray(result?.data)
      ? result.data
      : result?.deliveryAddress
        ? [result.deliveryAddress]
        : [];

  console.log("Source:", source);

  const tags = source.map((item) => item?.name || item?.tag).filter(Boolean);

  console.log("Tags:", tags);

  return [...new Set(tags)];
};

  const restaurantUserId = "A1001";

export default function AddressModal({
  isOpen,
  onClose,
  customerId = "",
  customerMobile = "",
  selectedAddress = "",
  onAddressSelect,
  savedAddressPath = "/account/address",
  masterTags: providedMasterTags = [],
  saveAddressExternally = false,
  openDirectAddForm = false,

  // Map se selected location
  initialLocationData = null,
}) {
  const { getCustomerList, addDeliveryAddress, getMasterDelivery } =
    useAddress();
  const { profileDetails } = useProfile();
  const {
    placesAutoCompletionApi,
    getLatLongByPlaceIdApi,
    placesAutoCompletionLoading,
    getLatLongByPlaceIdLoading,
  } = useGoogleLocationApi();
  const navigate = useNavigate();

  const {
    googleMapsApiKey,

    selectedDeliveryLocation,

    setSelectedDeliveryAddress,
    setSelectedDeliveryLocation,
  } = useGlobalContext();

  const [customer, setCustomer] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [masterTags, setMasterTags] = useState(providedMasterTags);

  const [isLoading, setIsLoading] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);

  const [headerSearchText, setHeaderSearchText] = useState("");
  const [headerSearchSuggestions, setHeaderSearchSuggestions] = useState([]);
  const [showHeaderSearchSuggestions, setShowHeaderSearchSuggestions] =
    useState(false);

  const [isHeaderSearching, setIsHeaderSearching] = useState(false);
  const [isHeaderLocationSelecting, setIsHeaderLocationSelecting] =
    useState(false);

  const headerSearchTimeoutRef = useRef(null);

  const [addressTag, setAddressTag] = useState("");
  const [addressTagOpen, setAddressTagOpen] = useState(false);
  const addressTagDropdownRef = useRef(null);
  const [customAddressLabel, setCustomAddressLabel] = useState("");
  const [customerName, setCustomerName] = useState("");

  const [mobileNumber, setMobileNumber] = useState(() =>
    normalizeMobileNumber(customerMobile),
  );

  useEffect(() => {
    if (!isOpen || !profileDetails) return;

    const fullName = [profileDetails?.first_name, profileDetails?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    if (fullName) {
      setCustomerName(fullName);
    }

    if (profileDetails?.phone) {
      setMobileNumber(normalizeMobileNumber(profileDetails.phone));
    }
  }, [isOpen, profileDetails]);

  const [houseNumber, setHouseNumber] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  useEffect(() => {
    if (!isOpen || !initialLocationData) {
      return;
    }

    const fillSelectedMapLocation = async () => {
      const finalAddress = normalizeString(initialLocationData?.address);

      const finalLatitude = String(initialLocationData?.latitude ?? "").trim();

      const finalLongitude = String(
        initialLocationData?.longitude ?? "",
      ).trim();

      // Map se aaye exact values
      setNewAddress(finalAddress);
      setLatitude(finalLatitude);
      setLongitude(finalLongitude);

      // Header me bhi same address
      setHeaderSearchText(finalAddress);

      setShowAddForm(true);

      setErrorMessage("");
      setSuccessMessage("");

      // City / State / Pincode / Landmark autofill
      if (finalLatitude && finalLongitude) {
        try {
          const addressDetails = await getAddressFromCoordinates(
            Number(finalLatitude),
            Number(finalLongitude),
          );

          setLandmark(addressDetails?.landmark || "");
          setCity(addressDetails?.city || "");
          setState(addressDetails?.state || "");
          setPinCode(addressDetails?.pinCode || "");
        } catch (error) {
          console.error("Unable to fill selected map address details:", error);
        }
      }
    };

    fillSelectedMapLocation();
  }, [isOpen, initialLocationData]);

  const [addressSuggestions, setAddressSuggestions] = useState([]);

  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  const [selectedPlaceId, setSelectedPlaceId] = useState("");
  const addressSearchTimeoutRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const effectiveMasterTags = useMemo(() => {
    const tags = [...providedMasterTags, ...masterTags]
      .map(normalizeString)
      .filter(Boolean);

    return [...new Set(tags)];
  }, [providedMasterTags, masterTags]);

  const handleHeaderLocationSearch = (value) => {
    setHeaderSearchText(value);
    setErrorMessage("");

    if (headerSearchTimeoutRef.current) {
      window.clearTimeout(headerSearchTimeoutRef.current);
    }

    const query = normalizeString(value);

    if (query.length < 4) {
      setHeaderSearchSuggestions([]);
      setShowHeaderSearchSuggestions(false);
      setIsHeaderSearching(false);

      return;
    }

    headerSearchTimeoutRef.current = window.setTimeout(async () => {
      try {
        setIsHeaderSearching(true);

        const storedLatitude = Number(selectedDeliveryLocation?.latitude);

        const storedLongitude = Number(selectedDeliveryLocation?.longitude);

        const searchLatitude =
          Number.isFinite(storedLatitude) && storedLatitude !== 0
            ? storedLatitude
            : 0;

        const searchLongitude =
          Number.isFinite(storedLongitude) && storedLongitude !== 0
            ? storedLongitude
            : 0;

        const result = await placesAutoCompletionApi({
          input: query,
          latitude: searchLatitude,
          longitude: searchLongitude,
          radius: 50000,
        });

        const suggestions = extractPlaceSuggestions(result);

        setHeaderSearchSuggestions(suggestions);

        setShowHeaderSearchSuggestions(suggestions.length > 0);
      } catch (error) {
        console.error("Header address search error:", error);

        setHeaderSearchSuggestions([]);
        setShowHeaderSearchSuggestions(false);
      } finally {
        setIsHeaderSearching(false);
      }
    }, 400);
  };

  const handleHeaderPlaceSelect = async (suggestion) => {
    if (!suggestion?.placeId) {
      return;
    }

    if (headerSearchTimeoutRef.current) {
      window.clearTimeout(headerSearchTimeoutRef.current);
    }

    // =====================================================
    // CLOSE DROPDOWN IMMEDIATELY
    // =====================================================

    setHeaderSearchText(suggestion.description);
    setHeaderSearchSuggestions([]);
    setShowHeaderSearchSuggestions(false);

    setIsHeaderLocationSelecting(true);
    setErrorMessage("");

    try {
      const result = await getLatLongByPlaceIdApi({
        placeId: suggestion.placeId,
      });

      const locationData = result?.response ?? result?.data ?? result;

      const locationItem = Array.isArray(locationData)
        ? locationData[0]
        : locationData;

      const finalLatitude = normalizeString(locationItem?.latitude);

      const finalLongitude = normalizeString(locationItem?.longitude);

      const finalAddress = normalizeString(
        locationItem?.address || suggestion.description,
      );

      const latitudeNumber = Number(finalLatitude);
      const longitudeNumber = Number(finalLongitude);

      if (
        !finalAddress ||
        !finalLatitude ||
        !finalLongitude ||
        !Number.isFinite(latitudeNumber) ||
        !Number.isFinite(longitudeNumber) ||
        (latitudeNumber === 0 && longitudeNumber === 0)
      ) {
        throw new Error("Unable to detect selected location.");
      }

      // Exact address after API response
      setHeaderSearchText(finalAddress);

      setSelectedDeliveryAddress(finalAddress);

      setSelectedDeliveryLocation({
        latitude: finalLatitude,
        longitude: finalLongitude,
      });

      onAddressSelect?.({
        address: finalAddress,
        addressLine: finalAddress,
        latitude: finalLatitude,
        longitude: finalLongitude,
        placeId: suggestion.placeId,
        source: "google_search",
      });

      console.log("HEADER SEARCH SELECTED LOCATION:", {
        address: finalAddress,
        placeId: suggestion.placeId,
        latitude: finalLatitude,
        longitude: finalLongitude,
      });

      onClose?.();
    } catch (error) {
      console.error("Header location select error:", error);

      setErrorMessage(error?.message || "Unable to select this location.");
    } finally {
      setIsHeaderLocationSelecting(false);
    }
  };

  const loadAllAddresses = useCallback(async () => {
    if (!customerMobile || !restaurantUserId) {
      setCustomer(null);
      setCustomerAddresses([]);
      setMasterTags([]);
      setErrorMessage(
        !customerMobile
          ? "Please login to continue"
          : "Restaurant userid is not available",
      );
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const [customerResult, masterResult] = await Promise.allSettled([
        getCustomerList({
          digit: customerMobile,
          userid: "A1001",
        }),
        getMasterDelivery({ id: "A1001" }),
      ]);

      // Load Master Tags
      if (masterResult.status === "fulfilled") {
        console.log("Master Result:", masterResult.value);

        setMasterTags(normalizeMasterTags(masterResult.value));
      } else {
        console.log("Master API Failed:", masterResult.reason);
      }

      // Load Customer Data
      if (customerResult.status === "fulfilled") {
        const result = customerResult.value;

        const customers = Array.isArray(result?.customers)
          ? result.customers
          : result?.customer
            ? [result.customer]
            : [];

        const currentCustomer = customers[0] || null;
        console.log("CURRENT CUSTOMER DATA:", currentCustomer);

        setCustomer(currentCustomer);
        setCustomerAddresses(extractCustomerAddresses(customers));

        setCustomerName(
          normalizeString(
            [profileDetails?.first_name, profileDetails?.last_name]
              .filter(Boolean)
              .join(" "),
          ),
        );
        setMobileNumber(
          normalizeMobileNumber(
            profileDetails?.phone ||
              currentCustomer?.custMobile ||
              currentCustomer?.customer_mobile ||
              currentCustomer?.mobile ||
              currentCustomer?.mobile_number ||
              currentCustomer?.number ||
              customerMobile,
          ),
        );
      } else {
        console.log("Customer API Failed:", customerResult.reason);

        setCustomer(null);
        setCustomerAddresses([]);
      }
    } catch (error) {
      setCustomer(null);
      setCustomerAddresses([]);
      setMasterTags([]);
      setErrorMessage(error?.message || "Unable to load delivery addresses");
    } finally {
      setIsLoading(false);
    }
  }, [
    customerMobile,
    restaurantUserId,
    getCustomerList,
    getMasterDelivery,
    profileDetails,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    loadAllAddresses();
  }, [isOpen, loadAllAddresses]);

  useEffect(() => {
    if (effectiveMasterTags.length > 0 && !addressTag) {
      setAddressTag(effectiveMasterTags[0]);
    }
  }, [effectiveMasterTags, addressTag]);

  useEffect(() => {
    if (!isOpen) {
      setShowAddForm(false);
      setShowMapPicker(false);
      setAddressTag("");
      setHouseNumber("");
      setNewAddress("");
      setLandmark("");
      setCity("");
      setState("");
      setPinCode("");
      setLatitude("");
      setLongitude("");
      setErrorMessage("");
      setSuccessMessage("");
      setCustomAddressLabel("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setShowAddForm(openDirectAddForm);
  }, [isOpen, openDirectAddForm]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [isOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        addressTagDropdownRef.current &&
        !addressTagDropdownRef.current.contains(event.target)
      ) {
        setAddressTagOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setMobileNumber(normalizeMobileNumber(customerMobile));
  }, [isOpen, customerMobile]);

  useEffect(() => {
    return () => {
      if (addressSearchTimeoutRef.current) {
        window.clearTimeout(addressSearchTimeoutRef.current);
      }

      if (headerSearchTimeoutRef.current) {
        window.clearTimeout(headerSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleGetCurrentLocation = async () => {
    setIsGettingLocation(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const coordinates = await getCurrentCoordinates();
      const currentLatitude = coordinates.latitude;
      const currentLongitude = coordinates.longitude;

      setLatitude(String(currentLatitude));
      setLongitude(String(currentLongitude));

      const detectedAddress = await getAddressFromCoordinates(
        currentLatitude,
        currentLongitude,
      );

      setNewAddress(detectedAddress.addressLine);
      setLandmark(detectedAddress.landmark);
      setCity(detectedAddress.city);
      setState(detectedAddress.state);
      setPinCode(detectedAddress.pinCode);
      setSuccessMessage("Current location and address detected successfully");
    } catch (error) {
      console.error("Current location error:", error);
      setErrorMessage(
        error?.message || "Unable to detect your current location address",
      );
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleOpenCurrentLocationForm = async () => {
    setShowAddForm(true);
    await handleGetCurrentLocation();
  };

  const handleOpenCurrentLocationMap = async () => {
    if (isGettingLocation) {
      return;
    }

    setIsGettingLocation(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Har click par current GPS
      const coordinates = await getCurrentCoordinates();

      const currentLatitude = String(coordinates.latitude);

      const currentLongitude = String(coordinates.longitude);

      console.log("CURRENT MAP LOCATION:", {
        latitude: currentLatitude,
        longitude: currentLongitude,
      });

      // Map ka initial point current GPS hoga
      setLatitude(currentLatitude);
      setLongitude(currentLongitude);

      // Previous search dropdown close
      setHeaderSearchSuggestions([]);
      setShowHeaderSearchSuggestions(false);

      // Same map modal open
      setShowMapPicker(true);
    } catch (error) {
      console.error("Current map location error:", error);

      setErrorMessage(
        error?.message || "Unable to detect your current location.",
      );
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleMapLocationConfirm = async ({
    address,
    latitude: mapLatitude,
    longitude: mapLongitude,
  }) => {
    const finalLatitude = String(mapLatitude ?? "").trim();

    const finalLongitude = String(mapLongitude ?? "").trim();

    if (!finalLatitude || !finalLongitude) {
      setErrorMessage("Unable to detect map location.");

      return;
    }

    let finalAddress = normalizeString(address);

    setLatitude(finalLatitude);

    setLongitude(finalLongitude);

    try {
      const addressDetails = await getAddressFromCoordinates(
        mapLatitude,
        mapLongitude,
      );

      if (!finalAddress) {
        finalAddress = normalizeString(addressDetails?.addressLine);
      }

      setLandmark(addressDetails?.landmark || "");

      setCity(addressDetails?.city || "");

      setState(addressDetails?.state || "");

      setPinCode(addressDetails?.pinCode || "");
    } catch (error) {
      console.error("Map address details error:", error);
    }

    setNewAddress(finalAddress);

    // =====================================================
    // SHOW MAP SELECTED ADDRESS IN TOP SEARCH BAR
    // =====================================================

    setHeaderSearchText(finalAddress);
    setHeaderSearchSuggestions([]);
    setShowHeaderSearchSuggestions(false);

    // =====================================================
    // GLOBAL SELECTED DELIVERY LOCATION
    // =====================================================

    setSelectedDeliveryAddress(finalAddress);

    setSelectedDeliveryLocation({
      latitude: finalLatitude,
      longitude: finalLongitude,
    });

    console.log("MAP SELECTED LOCATION:", {
      address: finalAddress,
      latitude: finalLatitude,
      longitude: finalLongitude,
    });

    // Map close
    setShowMapPicker(false);

    // Same form open karo jo "Add new address" button se open hota hai
    setShowAddForm(true);

    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleAddressSelect = async (item) => {
    const address = normalizeString(item?.address);

    if (!address) {
      return;
    }

    setIsSelecting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      let selectedLatitude = normalizeString(
        item?.latitude ?? item?.lat ?? item?.raw?.latitude ?? item?.raw?.lat,
      );

      let selectedLongitude = normalizeString(
        item?.longitude ??
          item?.lontitude ??
          item?.lng ??
          item?.raw?.longitude ??
          item?.raw?.lontitude ??
          item?.raw?.lng,
      );

      const latitudeNumber = Number(selectedLatitude);
      const longitudeNumber = Number(selectedLongitude);

      const hasValidCoordinates =
        selectedLatitude &&
        selectedLongitude &&
        Number.isFinite(latitudeNumber) &&
        Number.isFinite(longitudeNumber) &&
        !(latitudeNumber === 0 && longitudeNumber === 0);

      // =====================================================
      // SAVED ADDRESS HAS NO VALID LAT/LONG
      // Google se exact placeId + lat/long nikalo
      // =====================================================

      if (!hasValidCoordinates) {
        const autoCompleteResult = await placesAutoCompletionApi({
          input: address,
          latitude: 0,
          longitude: 0,
          radius: 50000,
        });

        const suggestions = extractPlaceSuggestions(autoCompleteResult);

        if (suggestions.length === 0) {
          throw new Error("Unable to find location for this saved address.");
        }

        const matchedPlace = suggestions[0];

        const locationResult = await getLatLongByPlaceIdApi({
          placeId: matchedPlace.placeId,
        });

        const locationData =
          locationResult?.response ?? locationResult?.data ?? locationResult;

        const locationItem = Array.isArray(locationData)
          ? locationData[0]
          : locationData;

        selectedLatitude = normalizeString(
          locationItem?.latitude ??
            locationItem?.lat ??
            locationItem?.location?.latitude ??
            locationItem?.location?.lat,
        );

        selectedLongitude = normalizeString(
          locationItem?.longitude ??
            locationItem?.lontitude ??
            locationItem?.lng ??
            locationItem?.location?.longitude ??
            locationItem?.location?.lontitude ??
            locationItem?.location?.lng,
        );

        if (
          !selectedLatitude ||
          !selectedLongitude ||
          !Number.isFinite(Number(selectedLatitude)) ||
          !Number.isFinite(Number(selectedLongitude)) ||
          (Number(selectedLatitude) === 0 && Number(selectedLongitude) === 0)
        ) {
          throw new Error(
            "Unable to get exact latitude and longitude for this address.",
          );
        }
      }

      // =====================================================
      // FINAL SELECTED ADDRESS + EXACT LAT/LONG
      // =====================================================

      setSelectedDeliveryAddress(address);

      setSelectedDeliveryLocation({
        latitude: selectedLatitude,
        longitude: selectedLongitude,
      });

      console.log("FINAL SELECTED SAVED ADDRESS:", {
        address,
        latitude: selectedLatitude,
        longitude: selectedLongitude,
      });

      onAddressSelect?.({
        ...item,
        address,
        latitude: selectedLatitude,
        longitude: selectedLongitude,
      });

      onClose?.();
    } catch (error) {
      console.error("Saved address selection error:", error);

      setErrorMessage(error?.message || "Unable to select delivery address");
    } finally {
      setIsSelecting(false);
    }
  };

  const handleAddressSearch = (value) => {
    setNewAddress(value);

    // User ne selected Google address edit kiya,
    // old coordinates ab valid nahi hain.
    setSelectedPlaceId("");

    setLatitude("");
    setLongitude("");

    setCity("");
    setState("");
    setPinCode("");
    setLandmark("");

    if (addressSearchTimeoutRef.current) {
      window.clearTimeout(addressSearchTimeoutRef.current);
    }

    if (value.trim().length < 4) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);

      return;
    }

    addressSearchTimeoutRef.current = window.setTimeout(async () => {
      const result = await placesAutoCompletionApi({
        input: value,
        latitude: 0,
        longitude: 0,
        radius: 50000,
      });

      const suggestions = extractPlaceSuggestions(result);

      setAddressSuggestions(suggestions);
      setShowAddressSuggestions(suggestions.length > 0);
    }, 400);
  };

  const handlePlaceSuggestionSelect = async (suggestion) => {
    if (!suggestion?.placeId) {
      return;
    }

    if (addressSearchTimeoutRef.current) {
      window.clearTimeout(addressSearchTimeoutRef.current);
    }

    setSelectedPlaceId(suggestion.placeId);

    setNewAddress(suggestion.description);

    setAddressSuggestions([]);
    setShowAddressSuggestions(false);

    setErrorMessage("");

    try {
      // -----------------------------------------------------
      // GET EXACT LAT / LONG FROM GOOGLE API
      // -----------------------------------------------------

      const result = await getLatLongByPlaceIdApi({
        placeId: suggestion.placeId,
      });

      const locationData = result?.response ?? result?.data ?? result;

      const locationItem = Array.isArray(locationData)
        ? locationData[0]
        : locationData;

      const resolvedLatitude = locationItem?.latitude ?? "";

      const resolvedLongitude = locationItem?.longitude ?? "";

      const resolvedAddress = normalizeString(
        locationItem?.address || suggestion.description,
      );

      if (resolvedLatitude === "" || resolvedLongitude === "") {
        setLatitude("");
        setLongitude("");

        setErrorMessage(
          "Unable to find latitude and longitude for this address.",
        );

        return;
      }

      const finalLatitude = String(resolvedLatitude);
      const finalLongitude = String(resolvedLongitude);

      setNewAddress(resolvedAddress);

      setLatitude(finalLatitude);
      setLongitude(finalLongitude);

      // IMPORTANT:
      // getLatLongByPlaceId ka exact result global selected location banao
      setSelectedDeliveryAddress(resolvedAddress);

      setSelectedDeliveryLocation({
        latitude: finalLatitude,
        longitude: finalLongitude,
      });

      // -----------------------------------------------------
      // AUTO FILL CITY / STATE / PIN / LANDMARK
      // -----------------------------------------------------

      try {
        const addressDetails = await getAddressFromCoordinates(
          resolvedLatitude,
          resolvedLongitude,
        );

        setCity(addressDetails?.city || "");
        setState(addressDetails?.state || "");
        setPinCode(addressDetails?.pinCode || "");
        setLandmark(addressDetails?.landmark || "");

        // Google suggestion ko address rehne do.
        // Reverse geocoder sirf remaining fields fill karega.
      } catch (addressError) {
        console.error("Address details autofill error:", addressError);
      }

      console.log("SELECTED GOOGLE ADDRESS:", {
        address: suggestion.description,
        placeId: suggestion.placeId,
        latitude: finalLatitude,
        longitude: finalLongitude,
      });
    } catch (error) {
      console.error("Place location error:", error);

      setLatitude("");
      setLongitude("");

      setErrorMessage("Unable to find location for selected address.");
    }
  };

  const handleSaveDeliveryAddress = async (event) => {
    event.preventDefault();

    const finalTag = normalizeString(addressTag || effectiveMasterTags[0]);
    const finalName = normalizeString(
      customerName ||
        customer?.custName ||
        customer?.customer_name ||
        customer?.name ||
        "Customer",
    );

    console.log("masterTags:", masterTags);
    console.log("effectiveMasterTags:", effectiveMasterTags);
    console.log("addressTag:", addressTag);
    console.log("finalTag:", finalTag);

    if (!restaurantUserId) {
      setErrorMessage("Restaurant userid is not available");
      return;
    }

    if (!customerMobile) {
      setErrorMessage("Customer mobile number is not available");
      return;
    }

    if (!finalTag) {
      setErrorMessage("Address tag is not available from master delivery API");
      return;
    }

    if (
      finalTag.toLowerCase() === "others" &&
      !normalizeString(customAddressLabel)
    ) {
      setErrorMessage("Please enter a custom address label.");
      return;
    }

    if (!normalizeString(newAddress)) {
      setErrorMessage("Enter your complete delivery address");
      return;
    }

    if (!normalizeString(city)) {
      setErrorMessage("Enter city");
      return;
    }

    if (!normalizeString(state)) {
      setErrorMessage("Enter state");
      return;
    }

    if (!normalizeString(pinCode)) {
      setErrorMessage("Enter pin code");
      return;
    }

    console.log({
      restaurantUserId,
      customerMobile,
      finalTag,
      finalName,
      newAddress,
      city,
      state,
      pinCode,
      latitude,
      longitude,
    });

    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const currentLatitude = Number(latitude);
      const currentLongitude = Number(longitude);

      if (
        !latitude ||
        !longitude ||
        !Number.isFinite(currentLatitude) ||
        !Number.isFinite(currentLongitude)
      ) {
        setErrorMessage(
          "Please select an address from the suggestions to detect its exact location.",
        );

        return;
      }

      const finalAddressLabel =
        finalTag.toLowerCase() === "others"
          ? normalizeString(customAddressLabel)
          : finalTag;

      const addressPayload = {
        userid: "A1001",
        custName: finalName,
        custMobile: customerMobile,
        tag: finalAddressLabel,
        addressTag: finalAddressLabel,
        address: {
          in_pin_code: pinCode,
          landmark_area: landmark,
          address: newAddress,
          city,
          state,
        },
        addressLine: normalizeString(newAddress),
        in_pin_code: normalizeString(pinCode),
        landmark_area: normalizeString(landmark),
        city: normalizeString(city),
        state: normalizeString(state),
        latitude: currentLatitude,
        longitude: currentLongitude,
        favourite: finalAddressLabel,
        isDefault: false,
        source: "customer",
      };

      if (saveAddressExternally) {
        await onAddressSelect?.(addressPayload);
      } else {
        console.log("Address Payload", addressPayload);
        await addDeliveryAddress(addressPayload);
        onAddressSelect?.({
          ...addressPayload,
          id: `new-${Date.now()}`,
          address: normalizeString(newAddress),
        });
      }

      setSelectedDeliveryAddress(normalizeString(newAddress));

      setSelectedDeliveryLocation({
        latitude: currentLatitude,
        longitude: currentLongitude,
      });

      setShowAddForm(false);
      setSuccessMessage("Delivery address added successfully");

      if (!saveAddressExternally) {
        await loadAllAddresses();
      }

      onClose?.();
    } catch (error) {
      setErrorMessage(error?.message || "Unable to save delivery address");
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewMore = () => {
    onClose?.();
    navigate(savedAddressPath, {
      state: {
        customerId,
        customerMobile,
        restaurantUserId,
        fromCheckout: true,
      },
    });
  };

  if (!isOpen) return null;

  const allAddresses = customerAddresses;
  const visibleAddresses = allAddresses.slice(0, 2);

  const modalContent = (
    <>
      <div
        className="
    fixed
    inset-0
    z-[99999]

    flex
    h-[100dvh]
    w-screen
    items-center
    justify-center

    overflow-hidden

    bg-black/60
    p-3

    backdrop-blur-[1px]

    sm:p-4
    lg:p-6
  "
      >
        <button
          type="button"
          aria-label="Close address modal"
          className="absolute inset-0 cursor-default"
          onClick={onClose}
        />

        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="address-modal-title"
          className="
    relative
    z-10

    flex
    w-full
    max-w-[620px]
    min-w-0
    flex-col
    overflow-hidden

    max-h-[calc(100dvh-24px)]

    rounded-[18px]
    bg-white
    shadow-2xl

    sm:max-h-[calc(100dvh-32px)]
    sm:rounded-[20px]

    lg:max-h-[calc(100dvh-48px)]
    lg:rounded-[22px]
  "
        >
          <div className="border-b border-[#e7ebf0] px-5 py-4 sm:px-6">
            {/* TITLE */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2
                  id="address-modal-title"
                  className="text-[20px] font-semibold text-[#253041]"
                >
                  Select delivery location
                </h2>

                <p className="mt-1 text-[13px] text-[#8e9bad]">
                  Choose a customer address or use your current location
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e2e7ed] text-[#657387] transition hover:bg-[#f5f7f9]"
              >
                <X size={19} />
              </button>
            </div>

            {/* LOCATION SEARCH */}
            <div className="relative mt-4">
              <div className="flex h-12 items-center gap-3 rounded-[11px] border border-[#dce2e9] bg-[#f8fafc] px-4 transition focus-within:border-[#08a66c] focus-within:bg-white">
                <Search size={19} className="shrink-0 text-[#7f8da0]" />

                <input
                  type="text"
                  value={headerSearchText}
                  onChange={(event) =>
                    handleHeaderLocationSearch(event.target.value)
                  }
                  onFocus={() => {
                    if (headerSearchSuggestions.length > 0) {
                      setShowHeaderSearchSuggestions(true);
                    }
                  }}
                  placeholder="Search area, street or landmark..."
                  autoComplete="off"
                  className="h-full min-w-0 flex-1 bg-transparent text-[14px] text-[#344054] outline-none placeholder:text-[#9aa5b4]"
                />

                {(isHeaderSearching || isHeaderLocationSelecting) && (
                  <Loader2
                    size={18}
                    className="shrink-0 animate-spin text-[#08a66c]"
                  />
                )}

                {headerSearchText &&
                  !isHeaderSearching &&
                  !isHeaderLocationSelecting && (
                    <button
                      type="button"
                      onClick={() => {
                        setHeaderSearchText("");
                        setHeaderSearchSuggestions([]);
                        setShowHeaderSearchSuggestions(false);
                      }}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#8b98aa] hover:bg-[#edf1f5]"
                    >
                      <X size={15} />
                    </button>
                  )}
              </div>

              {/* GOOGLE SUGGESTIONS */}
              {showHeaderSearchSuggestions &&
                headerSearchSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-[500] mt-2 max-h-[260px] overflow-y-auto rounded-[12px] border border-[#dce2e9] bg-white shadow-xl">
                    {headerSearchSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.placeId}
                        type="button"
                        disabled={isHeaderLocationSelecting}
                        onClick={() => handleHeaderPlaceSelect(suggestion)}
                        className="flex w-full items-start gap-3 border-b border-[#edf0f3] px-4 py-3.5 text-left transition last:border-b-0 hover:bg-[#f6faf8] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#eef8f5]">
                          <MapPin size={16} className="text-[#08a66c]" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium leading-5 text-[#344054]">
                            {suggestion.description}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

              {!isHeaderSearching &&
                headerSearchText.trim().length >= 3 &&
                showHeaderSearchSuggestions &&
                headerSearchSuggestions.length === 0 && (
                  <div className="absolute left-0 right-0 top-full z-[500] mt-2 rounded-[12px] border border-[#dce2e9] bg-white px-4 py-4 text-[13px] text-[#7b8798] shadow-xl">
                    No locations found
                  </div>
                )}
            </div>

            <div className="mt-3 grid w-full grid-cols-2 gap-3 sm:grid-cols-3">
              {/* ADD NEW ADDRESS */}
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);

                  setErrorMessage("");
                  setSuccessMessage("");

                  setHeaderSearchSuggestions([]);
                  setShowHeaderSearchSuggestions(false);

                  setShowMapPicker(true);
                }}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-[9px] border border-[#dfe5eb] bg-white px-3 text-[12px] font-semibold text-[#526174] transition hover:border-[#08a66c] hover:bg-[#f0fbf7] hover:text-[#08a66c] sm:px-4 sm:text-[13px]"
              >
                <Plus size={17} className="shrink-0" />

                <span className="truncate">Add new address</span>
              </button>

              {/* SEARCH MAP */}
              <button
                type="button"
                onClick={() => setShowMapPicker(true)}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-[9px] border border-[#dfe5eb] bg-white px-3 text-[12px] font-semibold text-[#526174] transition hover:bg-[#f6f8fa] sm:px-4 sm:text-[13px]"
              >
                <MapPin size={17} className="shrink-0" />
                <span className="truncate">Search Map</span>
              </button>

              {/* CURRENT LOCATION */}
              <button
                type="button"
                onClick={handleOpenCurrentLocationMap}
                disabled={isGettingLocation}
                className="col-span-2 inline-flex h-10 w-full items-center justify-center gap-2 rounded-[9px] border border-[#dfe5eb] bg-white px-4 text-[13px] font-semibold text-[#526174] transition hover:bg-[#f6f8fa] disabled:cursor-not-allowed disabled:opacity-50 sm:col-span-1"
              >
                {isGettingLocation ? (
                  <Loader2 size={17} className="shrink-0 animate-spin" />
                ) : (
                  <LocateFixed size={17} className="shrink-0" />
                )}

                {isGettingLocation ? "Finding location..." : "Current location"}
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 pb-[max(20px,env(safe-area-inset-bottom))] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:px-5 sm:pb-5">
            {errorMessage && (
              <div className="mb-4 flex items-center justify-between gap-3 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-600">
                <span className="min-w-0 flex-1">{errorMessage}</span>

                {errorMessage === "Please login to continue" && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose?.();
                      navigate("/login");
                    }}
                    className="
          shrink-0
          cursor-pointer
          font-semibold
          text-red-600
          transition
          hover:underline
          underline-offset-2
        "
                  >
                    Login
                  </button>
                )}
              </div>
            )}

            {successMessage && (
              <div className="mb-4 rounded-[10px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-700">
                {successMessage}
              </div>
            )}

            {showAddForm && (
              <form
                onSubmit={handleSaveDeliveryAddress}
                className="
      rounded-[16px]
      border
      border-[#dce3ea]
      bg-[#f8fafc]
      p-4
      sm:p-5
    "
              >
                {/* ================================================= */}
                {/* HEADER */}
                {/* ================================================= */}

                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-[15px] font-semibold text-[#344054]">
                      Address details
                    </h3>

                    <p className="text-[12px] leading-5 text-[#94a3b8]">
                      Map address is filled automatically. You can edit it
                      before saving.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setErrorMessage("");
                      setSuccessMessage("");
                      setShowMapPicker(true);
                    }}
                    className="
          inline-flex
          h-9
          shrink-0
          items-center
          justify-center
          rounded-[9px]
          border
          border-[#d8e0e8]
          bg-white
          px-3
          text-[11px]
          font-semibold
          text-[#526174]
          transition
          hover:border-[#08a66c]
          hover:text-[#08a66c]
          sm:px-4
        "
                  >
                    Change on map
                  </button>
                </div>

                {/* ================================================= */}
                {/* CUSTOMER NAME */}
                {/* ================================================= */}

                <div className="mt-5">
                  <label className="mb-1 block text-[12px] font-medium text-[#718096]">
                    Customer name
                  </label>

                  <input
                    type="text"
                    value={customerName}
                    onChange={(event) => {
                      setCustomerName(event.target.value);
                      setErrorMessage("");
                    }}
                    placeholder="Customer name"
                    autoComplete="name"
                    maxLength={80}
                    className="
          h-11
          w-full
          rounded-[9px]
          border
          border-[#d7dfe8]
          bg-white
          px-3
          text-[14px]
          text-[#344054]
          outline-none
          transition
          placeholder:text-[#98a2b3]
          focus:border-[#08a66c]
        "
                  />
                </div>

                {/* ================================================= */}
                {/* MOBILE + TAG */}
                {/* ================================================= */}

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* MOBILE */}

                  <div>
                    <label className="mb-1 block text-[12px] font-medium text-[#718096]">
                      Mobile number
                    </label>

                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      value={mobileNumber}
                      onChange={(event) => {
                        setMobileNumber(
                          normalizeMobileNumber(event.target.value),
                        );

                        setErrorMessage("");
                      }}
                      onPaste={(event) => {
                        event.preventDefault();

                        const pastedValue = event.clipboardData.getData("text");

                        setMobileNumber(normalizeMobileNumber(pastedValue));
                      }}
                      placeholder="10 digit mobile number"
                      maxLength={10}
                      className={`
            h-11
            w-full
            rounded-[9px]
            border
            bg-white
            px-3
            text-[14px]
            text-[#344054]
            outline-none
            transition
            placeholder:text-[#98a2b3]

            ${
              mobileNumber.length > 0 && !isValidIndianMobile(mobileNumber)
                ? "border-red-300 focus:border-red-400"
                : "border-[#d7dfe8] focus:border-[#08a66c]"
            }
          `}
                    />

                    {mobileNumber.length > 0 &&
                      !isValidIndianMobile(mobileNumber) && (
                        <p className="mt-1 text-[10px] text-red-500">
                          Enter a valid 10 digit mobile number.
                        </p>
                      )}
                  </div>

                  {/* ADDRESS TAG */}
                  <div>
                    <label className="mb-1 block text-[12px] font-medium text-[#718096]">
                      Address tag
                    </label>

                    <div ref={addressTagDropdownRef} className="relative z-50">
                      {/* SELECT BUTTON */}
                      <button
                        type="button"
                        onClick={() =>
                          setAddressTagOpen((previous) => !previous)
                        }
                        className="
        flex
        h-11
        w-full
        items-center
        justify-between
        rounded-[9px]
        border
        border-[#d7dfe8]
        bg-white
        px-3
        text-left
        text-[14px]
        text-[#344054]
        outline-none
        transition
        hover:border-[#c4ccd6]
        focus:border-[#08a66c]
      "
                      >
                        <span
                          className={`truncate ${
                            addressTag ? "text-[#344054]" : "text-[#98a2b3]"
                          }`}
                        >
                          {addressTag || "Select tag"}
                        </span>

                        <ChevronDown
                          size={16}
                          strokeWidth={2}
                          className={`
          ml-2
          shrink-0
          text-[#91a2ba]
          transition-transform
          duration-200
          ${addressTagOpen ? "rotate-180" : ""}
        `}
                        />
                      </button>

                      {/* DROPDOWN */}
                      {addressTagOpen && (
                        <div
                          className="
          absolute
          left-0
          right-0
          top-full
          z-[100]
          mt-[2px]
          max-h-[220px]
          overflow-y-auto
          rounded-[10px]
          border
          border-[#d7dfe8]
          bg-white
          py-1
          shadow-[0_8px_24px_rgba(15,23,42,0.12)]
        "
                        >
                          {effectiveMasterTags.map((tag) => {
                            const isSelected = addressTag === tag;

                            return (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => {
                                  setAddressTag(tag);
                                  setAddressTagOpen(false);
                                  setErrorMessage("");
                                }}
                                className={`
                block
                w-full
                px-3
                py-2.5
                text-left
                text-[14px]
                transition

                ${
                  isSelected
                    ? "bg-[#08a66c] text-white"
                    : "bg-white text-[#344054] hover:bg-[#f0fbf7]"
                }
              `}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ================================================= */}
                {/* CUSTOM TAG */}
                {/* ================================================= */}

                {addressTag.toLowerCase() === "others" && (
                  <div className="mt-4">
                    <label className="mb-1 block text-[12px] font-medium text-[#718096]">
                      Custom address label
                    </label>

                    <input
                      type="text"
                      value={customAddressLabel}
                      onChange={(event) =>
                        setCustomAddressLabel(event.target.value)
                      }
                      placeholder="Example: Gym, Friend's Home"
                      maxLength={50}
                      className="
            h-11
            w-full
            rounded-[9px]
            border
            border-[#d7dfe8]
            bg-white
            px-3
            text-[14px]
            text-[#344054]
            outline-none
            transition
            focus:border-[#08a66c]
          "
                    />
                  </div>
                )}

                {/* ================================================= */}
                {/* HOUSE / FLAT / OFFICE */}
                {/* ================================================= */}

                <div className="mt-4">
                  <label className="mb-1 block text-[12px] font-medium text-[#718096]">
                    Home / Flat / Office No.
                  </label>

                  <input
                    type="text"
                    value={houseNumber}
                    onChange={(event) => {
                      setHouseNumber(event.target.value);
                      setErrorMessage("");
                    }}
                    placeholder="Example: Flat 402, Office 18, H-24"
                    maxLength={100}
                    className="
          h-11
          w-full
          rounded-[9px]
          border
          border-[#d7dfe8]
          bg-white
          px-3
          text-[14px]
          text-[#344054]
          outline-none
          transition
          placeholder:text-[#98a2b3]
          focus:border-[#08a66c]
        "
                  />
                </div>

                {/* ================================================= */}
                {/* BUILDING / STREET / LANDMARK */}
                {/* ================================================= */}

                <div className="mt-4">
                  <label className="mb-1 block text-[12px] font-medium text-[#718096]">
                    Building / Street / Landmark
                  </label>

                  <input
                    type="text"
                    value={landmark}
                    onChange={(event) => {
                      setLandmark(event.target.value);
                      setErrorMessage("");
                    }}
                    placeholder="Building, street or nearby landmark"
                    maxLength={150}
                    className="
          h-11
          w-full
          rounded-[9px]
          border
          border-[#d7dfe8]
          bg-white
          px-3
          text-[14px]
          text-[#344054]
          outline-none
          transition
          placeholder:text-[#98a2b3]
          focus:border-[#08a66c]
        "
                  />
                </div>

                {/* ================================================= */}
                {/* COMPLETE ADDRESS */}
                {/* ================================================= */}

                <div className="relative mt-4">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <label className="text-[12px] font-medium text-[#718096]">
                      Complete address
                    </label>

                    {latitude && longitude && (
                      <span
                        className="
              inline-flex
              items-center
              rounded-full
              bg-[#eafaf4]
              px-2
              py-1
              text-[9px]
              font-semibold
              text-[#059669]
            "
                      >
                        Map location selected
                      </span>
                    )}
                  </div>

                  <div className="relative">
                    <textarea
                      value={newAddress}
                      onChange={(event) =>
                        handleAddressSearch(event.target.value)
                      }
                      onFocus={() => {
                        if (addressSuggestions.length > 0) {
                          setShowAddressSuggestions(true);
                        }
                      }}
                      placeholder="Complete delivery address"
                      rows={4}
                      autoComplete="off"
                      className="
            min-h-[96px]
            w-full
            resize-none
            rounded-[9px]
            border
            border-[#d7dfe8]
            bg-white
            px-3
            py-3
            pr-10
            text-[14px]
            leading-6
            text-[#344054]
            outline-none
            transition
            placeholder:text-[#98a2b3]
            focus:border-[#08a66c]
          "
                    />

                    {placesAutoCompletionLoading && (
                      <div className="absolute right-3 top-3">
                        <Loader2
                          size={17}
                          className="animate-spin text-[#08a66c]"
                        />
                      </div>
                    )}
                  </div>

                  {/* GOOGLE ADDRESS SUGGESTIONS */}

                  {showAddressSuggestions && addressSuggestions.length > 0 && (
                    <div
                      className="
              absolute
              left-0
              right-0
              top-full
              z-[100]
              mt-1
              max-h-[240px]
              overflow-y-auto
              rounded-[10px]
              border
              border-[#dce2e9]
              bg-white
              shadow-xl
            "
                    >
                      {addressSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.placeId}
                          type="button"
                          onClick={() =>
                            handlePlaceSuggestionSelect(suggestion)
                          }
                          className="
                  flex
                  w-full
                  items-start
                  gap-3
                  border-b
                  border-[#edf0f3]
                  px-4
                  py-3
                  text-left
                  transition
                  last:border-b-0
                  hover:bg-[#f5f8fa]
                "
                        >
                          <MapPin
                            size={17}
                            className="mt-0.5 shrink-0 text-[#08a66c]"
                          />

                          <span className="text-[13px] leading-5 text-[#344054]">
                            {suggestion.description}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {getLatLongByPlaceIdLoading && (
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-[#7c8999]">
                      <Loader2 size={14} className="animate-spin" />
                      Finding exact location...
                    </div>
                  )}
                </div>

                {/* ================================================= */}
                {/* FOOTER BUTTONS */}
                {/* ================================================= */}

                <div className="mt-2 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setErrorMessage("");
                    }}
                    className="
          h-10
          rounded-[9px]
          border
          border-[#d7dfe8]
          bg-white
          px-4
          text-[13px]
          font-semibold
          text-[#526174]
          transition
          hover:bg-[#f4f6f8]
        "
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isSaving}
                    className="
          inline-flex
          h-10
          items-center
          justify-center
          gap-2
          rounded-[9px]
          bg-[#08a66c]
          px-5
          text-[13px]
          font-semibold
          text-white
          transition
          hover:bg-[#078f5e]
          disabled:cursor-not-allowed
          disabled:opacity-60
        "
                  >
                    {isSaving && <Loader2 size={16} className="animate-spin" />}
                    Save address
                  </button>
                </div>
              </form>
            )}
            {!showAddForm && (
              <>
                {isLoading ? (
                  <div className="flex min-h-[230px] items-center justify-center">
                    <div className="flex flex-col items-center gap-3 text-[#8592a4]">
                      <Loader2 size={30} className="animate-spin" />
                      <p className="text-[13px]">
                        Loading customer addresses...
                      </p>
                    </div>
                  </div>
                ) : allAddresses.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-[14px] font-semibold text-[#344054]">
                        Customer addresses
                      </h3>

                      <button
                        type="button"
                        onClick={loadAllAddresses}
                        disabled={isLoading}
                        aria-label="Refresh customer addresses"
                        title="Refresh customer addresses"
                        className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-[#dfe5eb] bg-white px-2.5 text-[12px] font-semibold text-[#657387] transition hover:border-[#08a66c] hover:bg-[#f0fbf7] hover:text-[#08a66c] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <RefreshCcw
                          size={14}
                          className={isLoading ? "animate-spin" : ""}
                        />
                        Refresh
                      </button>
                    </div>

                    {visibleAddresses.map((item, index) => {
                      const LocationIcon = getLocationIcon(
                        item?.favourite || item?.tag,
                      );
                      const isSelected =
                        normalizeString(selectedAddress).toLowerCase() ===
                        normalizeString(item?.address).toLowerCase();

                      return (
                        <button
                          key={`${item?.id || "address"}-${index}`}
                          type="button"
                          disabled={isSelecting}
                          onClick={() => handleAddressSelect(item)}
                          className={`flex w-full items-start gap-3 rounded-[12px] border p-4 text-left transition disabled:cursor-not-allowed disabled:opacity-60 ${
                            isSelected
                              ? "border-[#08a66c] bg-[#f0fbf7]"
                              : "border-[#e2e7ed] bg-white hover:border-[#99a6b7] hover:bg-[#fafbfc]"
                          }`}
                        >
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                              isSelected
                                ? "bg-[#08a66c] text-white"
                                : "bg-[#eef2f6] text-[#66758a]"
                            }`}
                          >
                            <LocationIcon size={18} />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-[14px] font-semibold capitalize text-[#344054]">
                                {item?.favourite ||
                                  item?.tag ||
                                  "Delivery Address"}
                              </p>
                              {item?.isDefault && (
                                <span className="rounded-full bg-[#e8f8f2] px-2 py-0.5 text-[10px] font-semibold uppercase text-[#079765]">
                                  Default
                                </span>
                              )}
                            </div>
                            <p className="mt-1.5 text-[13px] leading-5 text-[#768396]">
                              {item?.address}
                            </p>
                          </div>

                          {isSelected && (
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#08a66c] text-white">
                              <Check size={14} strokeWidth={3} />
                            </div>
                          )}
                        </button>
                      );
                    })}

                    {allAddresses.length > 2 && (
                      <button
                        type="button"
                        onClick={handleViewMore}
                        className="mt-4 flex h-11 w-full items-center justify-center rounded-[10px] border border-[#dfe5eb] text-[13px] font-semibold text-[#526174] transition hover:border-[#08a66c] hover:bg-[#f0fbf7] hover:text-[#08a66c]"
                      >
                        View more addresses
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex min-h-[230px] flex-col items-center justify-center rounded-[14px] border border-dashed border-[#d9e0e8] bg-[#fafbfc] px-5 text-center">
                    <MapPin size={34} className="text-[#9aa7b8]" />
                    <h3 className="mt-3 text-[15px] font-semibold text-[#3c4756]">
                      No customer addresses found
                    </h3>
                    <p className="mt-1 max-w-[340px] text-[13px] leading-5 text-[#8b98aa]">
                      Use your current location to add a delivery address.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <MapLocationPicker
        isOpen={showMapPicker}
        onClose={() => setShowMapPicker(false)}
        apiKey={googleMapsApiKey}
        initialLatitude={latitude || selectedDeliveryLocation?.latitude}
        initialLongitude={longitude || selectedDeliveryLocation?.longitude}
        onConfirm={handleMapLocationConfirm}
      />
    </>
  );

  if (typeof document === "undefined") {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
}
